import { Info, LogOut, Plus, RotateCcw, Sparkles, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { NutrientTracking, SettingsView } from '../../shared/api';
import { GROUP_LABELS, isBaseKey, type GoalDirection, type NutrientGroup } from '../../shared/nutrients';
import { ACTIVITY, gramsFromPercents, macroPercents, maxPaceKg, type Activity } from '../../shared/targets';
import { cmToFtIn, formatWeight, ftInToCm, fromDisplayWeight, toDisplayWeight, type WeightUnit } from '../../shared/units';
import {
  useCreateNutrient,
  useDeleteNutrient,
  useResetAccount,
  useResetDemo,
  useSettings,
  useUpdateGoals,
  useUpdateProfile,
  useUpdateTracking,
  useSession,
  useSignOut,
} from '../api/hooks';
import { amount, directionWord, unitLabel } from '../lib/format';
import { parseDecimal } from '../../shared/numbers';
import { applyTheme, readTheme, type ThemePref } from '../lib/theme';
import { useUI } from '../lib/ui';
import { forgetGoogleAutoSelect } from './SignInPage';

function ProfileSection({ settings }: { settings: SettingsView }) {
  const { toast } = useUI();
  const update = useUpdateProfile();
  const p = settings.profile;
  const [unit, setUnit] = useState<WeightUnit>(p.weightUnit);
  const [sex, setSex] = useState(p.sex);
  const [birthYear, setBirthYear] = useState(String(p.birthYear));
  const [heightCm, setHeightCm] = useState(p.heightCm);
  const [activity, setActivity] = useState<Activity>(p.activity);
  const [goal, setGoal] = useState(p.goalKg !== null ? String(toDisplayWeight(p.goalKg, p.weightUnit)) : '');
  const [pace, setPace] = useState(p.paceKgPerWeek);
  const ftIn = cmToFtIn(heightCm);
  const cap = settings.currentKg ? maxPaceKg(settings.currentKg) : 1;
  const presets = (unit === 'lb' ? [0.5, 1, 1.5, 2].map((lb) => lb * 0.45359237) : [0.25, 0.5, 0.75, 1]).filter((kg) => kg <= cap + 1e-9);
  // Always offer the saved pace, even if it isn't one of this unit's presets.
  const paceOptions = pace > 0 && !presets.some((kg) => Math.abs(kg - pace) < 1e-6) ? [...presets, pace].sort((a, b) => a - b) : presets;

  const save = () => {
    // A typo must never clear the goal: NaN serializes to null.
    const goalText = goal.trim();
    const goalNum = goalText === '' ? null : parseDecimal(goalText);
    if (goalText !== '' && (goalNum === null || goalNum <= 0)) {
      toast(`Enter your goal weight as a number, like ${unit === 'lb' ? '165' : '72.5'}`, { tone: 'error' });
      return;
    }
    const year = Number(birthYear.trim());
    if (!Number.isInteger(year)) {
      toast('Enter your birth year, like 1988', { tone: 'error' });
      return;
    }
    update.mutate(
      {
        weightUnit: unit,
        sex,
        birthYear: year,
        heightCm: Math.round(heightCm * 10) / 10,
        activity,
        goalKg: goalNum === null ? null : fromDisplayWeight(goalNum, unit),
        paceKgPerWeek: pace,
      },
      { onSuccess: () => toast('Profile saved'), onError: (e) => toast(e.message, { tone: 'error' }) },
    );
  };

  return (
    <section className="card card--pad stack" style={{ gap: 16 }}>
      <h2 className="section-title">Profile &amp; goal</h2>
      <div className="grid-2">
        <label className="field">
          <span className="field__label">Sex (for energy estimates)</span>
          <select className="select" value={sex} onChange={(e) => setSex(e.target.value as typeof sex)}>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other / prefer not to say</option>
          </select>
        </label>
        <label className="field">
          <span className="field__label">Birth year</span>
          <input className="input num" inputMode="numeric" value={birthYear} onChange={(e) => setBirthYear(e.target.value)} />
        </label>
      </div>
      <div className="grid-2">
        <div className="field">
          <span className="field__label">Units</span>
          <div className="seg seg--block" role="radiogroup" aria-label="Weight unit">
            {(['lb', 'kg'] as const).map((u) => (
              <button
                key={u}
                role="radio"
                aria-checked={unit === u}
                className={`seg__btn ${unit === u ? 'is-active' : ''}`}
                onClick={() => {
                  const g = parseDecimal(goal);
                  if (g !== null && g > 0) setGoal(String(toDisplayWeight(fromDisplayWeight(g, unit), u)));
                  setUnit(u);
                }}
              >
                {u === 'lb' ? 'lb · ft' : 'kg · cm'}
              </button>
            ))}
          </div>
        </div>
        {unit === 'lb' ? (
          <div className="field">
            <span className="field__label">Height</span>
            <div className="row">
              <div className="input-affix grow">
                <input className="input num" inputMode="numeric" aria-label="Feet" value={ftIn.ft} onChange={(e) => setHeightCm(ftInToCm(Number(e.target.value) || 0, ftIn.inch))} />
                <span className="input-affix__suffix">ft</span>
              </div>
              <div className="input-affix grow">
                <input className="input num" inputMode="numeric" aria-label="Inches" value={ftIn.inch} onChange={(e) => setHeightCm(ftInToCm(ftIn.ft, Number(e.target.value) || 0))} />
                <span className="input-affix__suffix">in</span>
              </div>
            </div>
          </div>
        ) : (
          <label className="field">
            <span className="field__label">Height</span>
            <div className="input-affix">
              <input className="input num" inputMode="decimal" value={Math.round(heightCm)} onChange={(e) => setHeightCm(Number(e.target.value) || 0)} />
              <span className="input-affix__suffix">cm</span>
            </div>
          </label>
        )}
      </div>
      <label className="field">
        <span className="field__label">Activity</span>
        <select className="select" value={activity} onChange={(e) => setActivity(e.target.value as Activity)}>
          {Object.entries(ACTIVITY).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label} · {v.hint}
            </option>
          ))}
        </select>
      </label>
      <div className="grid-2">
        <label className="field">
          <span className="field__label">Goal weight</span>
          <div className="input-affix">
            <input className="input num" inputMode="decimal" value={goal} placeholder="optional" onChange={(e) => setGoal(e.target.value)} />
            <span className="input-affix__suffix">{unit}</span>
          </div>
        </label>
        <label className="field">
          <span className="field__label">Pace</span>
          <select className="select" value={String(pace)} onChange={(e) => setPace(Number(e.target.value))}>
            <option value="0">Maintain</option>
            {paceOptions.map((kg) => (
              <option key={kg} value={String(kg)}>
                {formatWeight(kg, unit, { decimals: unit === 'lb' ? 1 : 2 })} per week
              </option>
            ))}
          </select>
          <span className="field__hint">Capped at 1% of your weight per week.</span>
        </label>
      </div>
      <button className="btn btn--primary" style={{ alignSelf: 'flex-start' }} disabled={update.isPending} onClick={save}>
        Save profile
      </button>
    </section>
  );
}

function TargetsSection({ settings }: { settings: SettingsView }) {
  const { toast } = useUI();
  const update = useUpdateGoals();
  const [mode, setMode] = useState(settings.goals.mode);
  const [kcalText, setKcal] = useState(String(settings.goals.kcal));
  const [pct, setPct] = useState(() => macroPercents(settings.goals));
  useEffect(() => {
    setMode(settings.goals.mode);
    setKcal(String(settings.goals.kcal));
    setPct(macroPercents(settings.goals));
  }, [settings.goals]);
  const kcalNum = parseDecimal(kcalText) ?? 0;
  const kcalValid = Number.isInteger(kcalNum) && kcalNum > 0;
  const grams = gramsFromPercents(kcalNum, pct);
  const total = pct.protein + pct.carbs + pct.fat;
  const s = settings.suggested;

  return (
    <section className="card card--pad stack" style={{ gap: 16 }}>
      <h2 className="section-title">Daily targets</h2>
      <div className="seg seg--block" role="radiogroup" aria-label="Target mode" style={{ maxWidth: 360 }}>
        {(['suggested', 'custom'] as const).map((m) => (
          <button key={m} role="radio" aria-checked={mode === m} className={`seg__btn ${mode === m ? 'is-active' : ''}`} onClick={() => setMode(m)}>
            {m === 'suggested' ? 'Suggested' : 'Custom'}
          </button>
        ))}
      </div>

      {mode === 'suggested' ? (
        s ? (
          <div className="stack">
            <div className="grid-3">
              {[
                ['Calories', `${s.kcal.toLocaleString('en-US')}`, 'kcal'],
                ['Protein', `${s.protein}`, 'g'],
                ['Carbs · Fat', `${s.carbs} · ${s.fat}`, 'g'],
              ].map(([label, value, u]) => (
                <div key={label} className="card card--soft" style={{ padding: 12 }}>
                  <div className="stat__label">{label}</div>
                  <div className="stat__value num" style={{ fontSize: 22 }}>
                    {value} <span style={{ fontSize: 13 }}>{u}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="banner banner--ai">
              <Info size={17} />
              <ul className="small" style={{ margin: 0, paddingLeft: 16 }}>
                {s.explanation.map((line) => (
                  <li key={line}>{line}</li>
                ))}
                <li>Suggested targets update automatically as your trend weight changes.</li>
              </ul>
            </div>
          </div>
        ) : (
          <p className="small muted">Log a weigh-in to get suggested targets.</p>
        )
      ) : (
        <div className="stack">
          <label className="field" style={{ maxWidth: 220 }}>
            <span className="field__label">Calories</span>
            <div className="input-affix">
              <input className="input num" inputMode="numeric" value={kcalText} aria-invalid={!kcalValid} onChange={(e) => setKcal(e.target.value)} />
              <span className="input-affix__suffix">kcal</span>
            </div>
          </label>
          <div className="grid-3">
            {(['protein', 'carbs', 'fat'] as const).map((k) => (
              <label key={k} className="field">
                <span className="field__label" style={{ textTransform: 'capitalize' }}>
                  {k} · {grams[k]} g
                </span>
                <div className="input-affix">
                  <input
                    className="input input--sm num"
                    inputMode="numeric"
                    value={pct[k]}
                    onChange={(e) => setPct((p) => ({ ...p, [k]: Math.max(0, Math.min(100, Number(e.target.value) || 0)) }))}
                  />
                  <span className="input-affix__suffix">%</span>
                </div>
              </label>
            ))}
          </div>
          {!kcalValid && <p className="field__error">Enter calories as a whole number, like 1800.</p>}
          {total !== 100 && <p className="field__error">Percentages add up to {total}% — make them 100%.</p>}
        </div>
      )}
      <button
        className="btn btn--primary"
        style={{ alignSelf: 'flex-start' }}
        disabled={update.isPending || (mode === 'custom' && (total !== 100 || !kcalValid))}
        onClick={() =>
          update.mutate(mode === 'suggested' ? { mode } : { mode, kcal: kcalNum, protein: grams.protein, carbs: grams.carbs, fat: grams.fat }, {
            onSuccess: () => toast('Targets saved'),
            onError: (e) => toast(e.message, { tone: 'error' }),
          })
        }
      >
        Save targets
      </button>
    </section>
  );
}

function NutrientsSection({ settings }: { settings: SettingsView }) {
  const { toast } = useUI();
  const saveTracking = useUpdateTracking();
  const create = useCreateNutrient();
  const remove = useDeleteNutrient();
  const [rows, setRows] = useState<Map<string, NutrientTracking>>(() => new Map(settings.tracking.map((t) => [t.key, t])));
  const [filter, setFilter] = useState('');
  const [newName, setNewName] = useState('');
  const [newUnit, setNewUnit] = useState('mg');
  const [newDir, setNewDir] = useState<GoalDirection>('max');
  const [newTarget, setNewTarget] = useState('');
  // What's typed in each target box, so "0." and "1,5" survive while typing.
  const [targetText, setTargetText] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    setRows(new Map(settings.tracking.map((t) => [t.key, t])));
    setTargetText(new Map());
  }, [settings.tracking]);
  const invalidTargets = [...targetText].filter(([key, text]) => {
    if (!rows.get(key)?.tracked || !text.trim()) return false;
    const n = parseDecimal(text);
    return n === null || n < 0;
  });

  const dirty = useMemo(
    () =>
      settings.tracking.filter((t) => {
        const r = rows.get(t.key);
        return r && (r.tracked !== t.tracked || r.target !== t.target || r.direction !== t.direction);
      }),
    [rows, settings.tracking],
  );
  const set = (key: string, patch: Partial<NutrientTracking>) =>
    setRows((prev) => {
      const next = new Map(prev);
      const cur = next.get(key);
      if (cur) next.set(key, { ...cur, ...patch });
      return next;
    });

  const groups = Object.keys(GROUP_LABELS) as NutrientGroup[];
  const q = filter.trim().toLowerCase();

  return (
    <section className="card card--pad stack" style={{ gap: 14 }}>
      <div>
        <h2 className="section-title">Nutrients</h2>
        <p className="small muted" style={{ marginTop: 4 }}>
          Calories, protein, carbs and fat are always tracked. Turn on anything else — or add your own — and it shows up on Today, in AI estimates and in
          Coach.
        </p>
      </div>
      <input className="input input--sm" type="search" placeholder="Find a nutrient…" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Find a nutrient" />
      <div>
        {groups.map((group) => {
          const defs = settings.nutrients.filter((n) => n.group === group && !isBaseKey(n.key) && (!q || n.name.toLowerCase().includes(q)));
          if (defs.length === 0) return null;
          return (
            <div className="ngroup" key={group}>
              <div className="ngroup__title">{GROUP_LABELS[group]}</div>
              {defs.map((def) => {
                const row = rows.get(def.key);
                if (!row) return null;
                return (
                  <div className="nrow" key={def.key}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{def.name}</div>
                      <div className="tiny muted">
                        {unitLabel(def)}
                        {def.dv ? ` · daily value ${amount(def.dv, def)} ${unitLabel(def)}` : ''}
                      </div>
                    </div>
                    <div className="row">
                      {def.custom && (
                        <button
                          className="btn btn--quiet btn--icon btn--xs"
                          aria-label={`Delete ${def.name}`}
                          onClick={() => remove.mutate(def.key, { onSuccess: () => toast(`Removed ${def.name}`), onError: (e) => toast(e.message, { tone: 'error' }) })}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      <button
                        className="switch"
                        role="switch"
                        aria-checked={row.tracked}
                        aria-label={`Track ${def.name}`}
                        onClick={() => set(def.key, { tracked: !row.tracked, target: row.target ?? def.dv ?? null })}
                      />
                    </div>
                    {row.tracked && (
                      <div className="nrow__edit">
                        <select className="select input--sm" value={row.direction} aria-label={`${def.name} goal type`} onChange={(e) => set(def.key, { direction: e.target.value as GoalDirection })}>
                          {(['min', 'max', 'target'] as const).map((d) => (
                            <option key={d} value={d}>
                              {directionWord(d)}
                            </option>
                          ))}
                        </select>
                        <div className="input-affix">
                          <input
                            className="input input--sm num"
                            inputMode="decimal"
                            aria-label={`${def.name} target`}
                            placeholder="no target"
                            value={targetText.get(def.key) ?? (row.target === null ? '' : String(row.target))}
                            aria-invalid={invalidTargets.some(([k]) => k === def.key)}
                            onChange={(e) => {
                              const text = e.target.value;
                              setTargetText((prev) => new Map(prev).set(def.key, text));
                              const n = parseDecimal(text);
                              if (!text.trim()) set(def.key, { target: null });
                              else if (n !== null && n >= 0) set(def.key, { target: n });
                            }}
                          />
                          <span className="input-affix__suffix">{unitLabel(def)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {invalidTargets.length > 0 && <p className="field__error">Targets must be numbers, like 25 or 2.5.</p>}
      {dirty.length > 0 && (
        <div className="row" style={{ position: 'sticky', bottom: 'calc(var(--tabbar-h) + 12px)', zIndex: 2 }}>
          <button
            className="btn btn--primary"
            disabled={saveTracking.isPending || invalidTargets.length > 0}
            onClick={() =>
              saveTracking.mutate(
                { items: dirty.map((t) => rows.get(t.key)!).map(({ key, tracked, target, direction }) => ({ key, tracked, target, direction })) },
                { onSuccess: () => toast('Nutrients saved'), onError: (e) => toast(e.message, { tone: 'error' }) },
              )
            }
          >
            Save {dirty.length} {dirty.length === 1 ? 'change' : 'changes'}
          </button>
          <button
            className="btn btn--ghost"
            onClick={() => {
              setRows(new Map(settings.tracking.map((t) => [t.key, t])));
              setTargetText(new Map());
            }}
          >
            Cancel
          </button>
        </div>
      )}
      <form
        className="card card--soft stack"
        style={{ padding: 14, gap: 10 }}
        onSubmit={(e) => {
          e.preventDefault();
          if (!newName.trim()) return;
          const target = newTarget.trim() === '' ? null : parseDecimal(newTarget);
          if (newTarget.trim() !== '' && (target === null || target < 0)) {
            toast('Targets must be numbers, like 25 or 2.5.', { tone: 'error' });
            return;
          }
          create.mutate(
            { name: newName.trim(), unit: newUnit, direction: newDir, target },
            {
              onSuccess: () => {
                toast(`Now tracking ${newName.trim()}`);
                setNewName('');
                setNewTarget('');
              },
              onError: (err) => toast(err.message, { tone: 'error' }),
            },
          );
        }}
      >
        <div style={{ fontWeight: 650 }}>Add your own nutrient</div>
        <div className="row wrap">
          <input className="input input--sm grow" style={{ minWidth: 160 }} placeholder="e.g. Water, Omega-6, Lutein" value={newName} onChange={(e) => setNewName(e.target.value)} aria-label="Nutrient name" />
          <select className="select input--sm" style={{ width: 'auto' }} value={newUnit} onChange={(e) => setNewUnit(e.target.value)} aria-label="Unit">
            {['g', 'mg', 'µg', 'IU', 'ml', 'unit'].map((u) => (
              <option key={u}>{u}</option>
            ))}
          </select>
          <select className="select input--sm" style={{ width: 'auto' }} value={newDir} onChange={(e) => setNewDir(e.target.value as GoalDirection)} aria-label="Goal type">
            {(['min', 'max', 'target'] as const).map((d) => (
              <option key={d} value={d}>
                {directionWord(d)}
              </option>
            ))}
          </select>
          <input className="input input--sm num" style={{ width: 110 }} inputMode="decimal" placeholder="target" value={newTarget} onChange={(e) => setNewTarget(e.target.value)} aria-label="Target" />
          <button className="btn btn--primary btn--sm" disabled={!newName.trim() || create.isPending}>
            <Plus size={15} /> Add
          </button>
        </div>
        <p className="tiny muted">Values come from foods you create, nutrition labels you scan, and AI estimates.</p>
      </form>
    </section>
  );
}

export function YouPage() {
  const { today, toast } = useUI();
  const settings = useSettings();
  const reset = useResetDemo();
  const resetAccount = useResetAccount();
  const [theme, setTheme] = useState<ThemePref>(readTheme);
  const [confirmReset, setConfirmReset] = useState(false);
  const [eraseText, setEraseText] = useState('');
  const session = useSession();
  const signOut = useSignOut();
  const data = settings.data;

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">You</h1>
          <p className="page__sub">Goals, nutrients, and how the app works for you.</p>
        </div>
      </div>
      {!data ? (
        <div className="skeleton" style={{ height: 320, borderRadius: 20 }} />
      ) : (
        <>
          <ProfileSection key={`p-${data.profile.weightUnit}-${data.profile.goalKg}`} settings={data} />
          <TargetsSection settings={data} />
          <NutrientsSection settings={data} />

          <section className="card card--pad stack" style={{ gap: 12 }}>
            <h2 className="section-title">Appearance</h2>
            <div className="seg" role="radiogroup" aria-label="Theme" style={{ alignSelf: 'flex-start' }}>
              {(['system', 'light', 'dark'] as const).map((t) => (
                <button
                  key={t}
                  role="radio"
                  aria-checked={theme === t}
                  className={`seg__btn ${theme === t ? 'is-active' : ''}`}
                  onClick={() => {
                    setTheme(t);
                    applyTheme(t);
                  }}
                >
                  {t[0]!.toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </section>

          <section className="card card--pad stack" style={{ gap: 12 }}>
            <h2 className="section-title">Food data</h2>
            <div className="row wrap">
              <span className={`badge ${data.foods.source === 'fatsecret' ? 'badge--good' : ''}`}>{data.foods.source === 'fatsecret' ? 'FatSecret' : 'Built-in catalog'}</span>
              <span className="small muted">
                Barcodes via <b>{data.foods.barcode === 'fatsecret' ? 'FatSecret' : 'Open Food Facts'}</b>
              </span>
            </div>
            <p className="small muted">
              {data.foods.source === 'fatsecret'
                ? 'Search and AI matching use FatSecret; nutrition is converted to per-100 g so grams stay the base unit. Your log stays in this app.'
                : 'Using the built-in catalog of ~220 common foods. Add FATSECRET_CLIENT_ID and FATSECRET_CLIENT_SECRET to .env to search FatSecret.'}
            </p>
          </section>

          <section className="card card--pad stack" style={{ gap: 12 }}>
            <h2 className="section-title">AI</h2>
            <div className="row wrap">
              <span className={`badge ${data.ai.enabled ? 'badge--good' : 'badge--warn'}`}>{data.ai.enabled ? 'On' : 'Off'}</span>
              <span className="small muted">
                Provider <b>{data.ai.provider}</b> · fast model <code>{data.ai.model}</code> · Coach model <code>{data.ai.smartModel}</code>
              </span>
            </div>
            {data.ai.provider === 'stub' && (
              <div className="banner banner--warn">
                <Sparkles size={17} />
                <span className="small">
                  <b>Demo mode.</b> AI answers are simulated offline from your data and the built-in food list. Set <code>AI_PROVIDER=gemini</code> and{' '}
                  <code>GEMINI_API_KEY</code> in .env for real AI.
                </span>
              </div>
            )}
            <div className="banner banner--ai">
              <Sparkles size={17} />
              <div className="small stack" style={{ gap: 6 }}>
                <span>
                  AI features send Google Gemini only what each one needs: your food descriptions and photos, food names and grams from your log, dated
                  weigh-ins, profile basics (sex, age, height, activity, goal and targets) and your Coach conversation. It never sees a name or email. Photos
                  are shrunk and stripped of location data in your browser and are never stored. Voice input is transcribed by your browser's speech
                  service, which may send the audio to Google or Apple.
                </span>
                <span>
                  Gemini's free tier may use prompts to improve Google's products; a billing-enabled key avoids that. There's no HIPAA agreement in place, so
                  keep this to your own data — never anyone else's health information.
                </span>
              </div>
            </div>
          </section>

          {session.data?.required && (
            <section className="card card--pad row between wrap" style={{ gap: 12 }}>
              <div>
                <h2 className="section-title">Account</h2>
                <p className="small muted">Signed in as {session.data.email}</p>
              </div>
              <div className="row wrap">
                <button
                  className="btn btn--quiet btn--sm"
                  disabled={signOut.isPending}
                  title="Ends every session for this account, on all devices"
                  onClick={() => {
                    forgetGoogleAutoSelect();
                    signOut.mutate({ everywhere: true }, { onError: (e) => toast(e.message, { tone: 'error' }) });
                  }}
                >
                  Sign out everywhere
                </button>
                <button
                  className="btn btn--ghost"
                  disabled={signOut.isPending}
                  onClick={() => {
                    forgetGoogleAutoSelect();
                    signOut.mutate({}, { onError: (e) => toast(e.message, { tone: 'error' }) });
                  }}
                >
                  <LogOut size={16} /> Sign out
                </button>
              </div>
            </section>
          )}

          <section className="card card--pad stack" style={{ gap: 12 }}>
            <h2 className="section-title">Your data</h2>
            {data.account.mode === 'demo' ? (
              <>
                <p className="small muted">This is the synthetic demo account. Reset regenerates ten weeks of sample weigh-ins and meals.</p>
                {confirmReset ? (
                  <div className="row wrap">
                    <button
                      className="btn btn--danger"
                      disabled={reset.isPending}
                      onClick={() =>
                        reset.mutate(today, {
                          onSuccess: () => {
                            toast('Demo data reset');
                            setConfirmReset(false);
                          },
                          onError: (e) => toast(e.message, { tone: 'error' }),
                        })
                      }
                    >
                      Yes, erase and reseed
                    </button>
                    <button className="btn btn--ghost" onClick={() => setConfirmReset(false)}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button className="btn btn--ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setConfirmReset(true)}>
                    <RotateCcw size={16} /> Reset demo data
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="small muted">
                  Your log lives on this computer in <code>data/grams.db</code> (git-ignored). Copy that file to back it up.
                </p>
                <div className="row wrap">
                  <input
                    className="input input--sm"
                    style={{ maxWidth: 220 }}
                    placeholder='Type "erase" to confirm'
                    aria-label='Type "erase" to confirm starting over'
                    value={eraseText}
                    onChange={(e) => setEraseText(e.target.value)}
                  />
                  <button
                    className="btn btn--danger btn--sm"
                    disabled={eraseText.trim().toLowerCase() !== 'erase' || resetAccount.isPending}
                    onClick={() => resetAccount.mutate(undefined, { onError: (e) => toast(e.message, { tone: 'error' }) })}
                  >
                    <RotateCcw size={15} /> Erase everything and start over
                  </button>
                </div>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}
